//codex-- Personal templates live at the top of the New Operation Schedule form and never submit the schedule themselves.
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { ChevronDownIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { applyOperationTemplate, createOperationTemplateApi } from './operationTemplateApi';

//codex-- Mount only for a new schedule so closing or switching to Edit discards template-management UI state.
export default function OperationTemplateControls({ apiUrl, formData, setFormData }) {
  const api = useMemo(() => createOperationTemplateApi(apiUrl), [apiUrl]);
  const navigate = useNavigate();
  const inputId = useId();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [busy, setBusy] = useState(false);
  const [retry, setRetry] = useState(0);
  const mounted = useRef(false);
  const pending = useRef(false);
  const buttonClass = 'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 disabled:cursor-not-allowed disabled:opacity-50';

  //codex-- Abort list requests when the modal closes; retain the label and schedule draft on mutation failures.
  useEffect(() => {
    mounted.current = true;
    const controller = new AbortController();
    setLoading(true); setError('');
    api.list(controller.signal).then((rows) => {
      if (!controller.signal.aborted) setTemplates(rows);
    }).catch((failure) => {
      if (controller.signal.aborted) return;
      if (failure.response?.status === 401) navigate('/login');
      else setError(failure.response?.data?.error || failure.response?.data?.message || failure.message || 'Unable to load templates.');
    }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => { mounted.current = false; controller.abort(); };
  }, [api, navigate, retry]);

  //codex-- One click opens a label entry; saving requires a non-empty label of at most 200 characters.
  const save = async () => {
    const name = label.trim();
    if (!name || Array.from(name).length > 200) { toast.error('Enter a template label of 1–200 characters.', { position: 'top-center' }); return; }
    if (pending.current || loading) return;
    pending.current = true; setBusy(true);
    try {
      const template = await api.save(name, formData);
      if (!mounted.current) return;
      setTemplates((rows) => [...rows, template]);
      setSelectedId(String(template.id)); setEditorOpen(false); setLabel('');
      toast.success('Template saved.', { position: 'top-center' });
    } catch (failure) {
      if (mounted.current) {
        if (failure.response?.status === 401) navigate('/login');
        else toast.error(failure.response?.data?.error || failure.response?.data?.message || failure.message || 'Unable to save template.', { position: 'top-center' });
      }
    } finally { pending.current = false; if (mounted.current) setBusy(false); }
  };

  //codex-- Delete only the chosen template; the existing form values and any already-created schedules stay intact.
  const remove = async (template) => {
    if (pending.current) return;
    pending.current = true; setBusy(true);
    try {
      await api.remove(template.id);
      if (!mounted.current) return;
      setTemplates((rows) => rows.filter((row) => row.id !== template.id));
      setSelectedId((id) => id === String(template.id) ? '' : id);
      toast.success('Template deleted.', { position: 'top-center' });
    } catch (failure) {
      if (mounted.current) {
        if (failure.response?.status === 401) navigate('/login');
        else toast.error(failure.response?.data?.error || failure.response?.data?.message || failure.message || 'Unable to delete template.', { position: 'top-center' });
      }
    } finally { pending.current = false; if (mounted.current) setBusy(false); }
  };
  const selected = templates.find((row) => String(row.id) === selectedId);

  return (
    <section aria-label="Personal operation templates" className="rounded-xl border border-blue-100 bg-blue-50/50 p-3">
      {/* //codex-- Start with a save button; after the first saved template offer selection, additions, and individual deletion. */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-semibold text-slate-700">My templates</span>
        {!templates.length ? <button type="button" disabled={loading || busy || Boolean(error)} onClick={() => setEditorOpen(true)} className={`${buttonClass} border-blue-200 bg-blue-100 text-blue-900`}><PlusIcon aria-hidden="true" className="h-4 w-4" />{loading ? 'Loading templates...' : 'Save as template'}</button> : (
          <Popover className="min-w-0 max-w-full">
            {({ close }) => <>
              <PopoverButton type="button" disabled={loading || busy} className={`${buttonClass} max-w-full border-blue-200 bg-blue-100 text-blue-900`}><span className="truncate">{selected?.label || 'Choose a template'}</span><ChevronDownIcon aria-hidden="true" className="h-4 w-4 shrink-0" /></PopoverButton>
              <PopoverPanel focus anchor={{ to: 'bottom end', gap: 8, padding: 12 }} className="z-50 w-80 max-w-[calc(100vw-24px)] overflow-y-auto rounded-xl border border-slate-200 bg-white p-2 shadow-xl [--anchor-max-height:22rem]">
                <button type="button" disabled={busy} onClick={() => { setEditorOpen(true); close(); }} className={`${buttonClass} mb-2 w-full border-blue-100 bg-blue-50 text-blue-800`}><PlusIcon aria-hidden="true" className="h-4 w-4" />Save new template</button>
                {templates.map((template) => <div key={template.id} className="flex min-w-0 items-center gap-1 border-t border-slate-100 py-1">
                  <button type="button" disabled={busy} aria-pressed={selectedId === String(template.id)} onClick={() => { setFormData((form) => applyOperationTemplate(form, template)); setSelectedId(String(template.id)); setEditorOpen(false); close(); }} className={`${buttonClass} min-w-0 flex-1 justify-start break-words border-transparent text-left ${selectedId === String(template.id) ? 'bg-blue-100 text-blue-900' : 'text-slate-700 hover:bg-slate-50'}`}>{template.label}</button>
                  <button type="button" disabled={busy} aria-label={`Delete template ${template.label}`} onClick={() => remove(template)} className={`${buttonClass} shrink-0 border-transparent text-red-600 hover:bg-red-50`}><TrashIcon aria-hidden="true" className="h-4 w-4" /></button>
                </div>)}
              </PopoverPanel>
            </>}
          </Popover>
        )}
      </div>
      {error && <div role="alert" className="mt-2 text-sm text-red-700">{error}<button type="button" onClick={() => setRetry((value) => value + 1)} className={`${buttonClass} ml-2 border-red-200 bg-white`}>Try again</button></div>}
      {/* //codex-- Ask only for the label after an explicit save action; Enter saves the template rather than creating a schedule. */}
      {editorOpen && <div className="mt-3 border-t border-blue-100 pt-3">
        <label htmlFor={inputId} className="block text-sm font-medium text-slate-700">Template label</label>
        <input id={inputId} autoFocus type="text" maxLength={200} aria-describedby={`${inputId}-limit`} value={label} disabled={busy} onChange={(event) => setLabel(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); event.stopPropagation(); save(); } }} className="mt-1 min-h-[44px] w-full rounded-lg border-slate-300 text-base focus:border-blue-500 focus:ring-blue-500" />
        <p id={`${inputId}-limit`} className="mt-1 text-xs text-slate-500">{Array.from(label).length}/200 characters</p>
        <div className="mt-2 flex flex-wrap justify-end gap-2"><button type="button" disabled={busy} onClick={() => { setEditorOpen(false); setLabel(''); }} className={`${buttonClass} border-slate-200 bg-white text-slate-700`}>Cancel template</button><button type="button" disabled={busy || !label.trim() || loading || Boolean(error)} onClick={save} className={`${buttonClass} border-blue-200 bg-blue-100 text-blue-900`}>{busy ? 'Saving...' : 'Save template'}</button></div>
      </div>}
    </section>
  );
}
